import { useState } from "react";
import { Plus, Edit2, Trash2, Image, ToggleLeft, ToggleRight, X, Link as LinkIcon } from "lucide-react";

const INITIAL_BANNERS = [
  { id: 1, title: "Summer Sale — Up to 40% Off",   subtitle: "Limited time offer on all burgers",  link: "/menu",   position: "Hero",    status: "Active",   bg: "from-orange-400 to-red-400" },
  { id: 2, title: "New Arrivals: Spring Menu",      subtitle: "Freshly crafted seasonal specials",  link: "/menu",   position: "Hero",    status: "Active",   bg: "from-green-400 to-teal-400" },
  { id: 3, title: "Free Delivery on Orders ৳500+", subtitle: "Valid on all online orders today",   link: "/checkout",position: "Promo Bar",status: "Active",  bg: "from-blue-400 to-indigo-400" },
  { id: 4, title: "Loyalty Rewards Program",        subtitle: "Earn points on every order",         link: "/profile",position: "Popup",   status: "Inactive", bg: "from-purple-400 to-pink-400" },
  { id: 5, title: "Weekend Brunch Special",         subtitle: "Every Saturday & Sunday 10am–2pm",   link: "/menu",   position: "Hero",    status: "Inactive", bg: "from-yellow-400 to-orange-400" },
];

const POSITIONS = ["Hero", "Promo Bar", "Popup", "Sidebar"];
const BG_OPTIONS = [
  { label: "Orange → Red",    value: "from-orange-400 to-red-400" },
  { label: "Green → Teal",    value: "from-green-400 to-teal-400" },
  { label: "Blue → Indigo",   value: "from-blue-400 to-indigo-400" },
  { label: "Purple → Pink",   value: "from-purple-400 to-pink-400" },
  { label: "Yellow → Orange", value: "from-yellow-400 to-orange-400" },
];

const Banners = () => {
  const [banners, setBanners] = useState(INITIAL_BANNERS);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const initialForm = { title: "", subtitle: "", link: "/menu", position: "Hero", status: "Active", bg: "from-orange-400 to-red-400" };
  const [formData, setFormData] = useState(initialForm);

  const handleOpen = (banner = null) => {
    if (banner) {
      setEditingId(banner.id);
      setFormData({ title: banner.title, subtitle: banner.subtitle, link: banner.link, position: banner.position, status: banner.status, bg: banner.bg });
    } else {
      setEditingId(null);
      setFormData(initialForm);
    }
    setIsModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingId) {
      setBanners(banners.map((b) => b.id === editingId ? { ...b, ...formData } : b));
    } else {
      setBanners([{ id: Date.now(), ...formData }, ...banners]);
    }
    setIsModalOpen(false);
  };

  const toggleStatus = (id) => {
    setBanners(banners.map((b) => b.id === id ? { ...b, status: b.status === "Active" ? "Inactive" : "Active" } : b));
  };

  const handleDelete = (id) => setBanners(banners.filter((b) => b.id !== id));

  return (
    <div className="space-y-8">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Banners & Promotions</h1>
          <p className="text-gray-500 text-sm">Manage homepage banners and promotional messages.</p>
        </div>
        <button
          onClick={() => handleOpen()}
          className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-orange-200 flex items-center gap-2 transition active:scale-95"
        >
          <Plus size={20} /> Add Banner
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {[
          { label: "Total Banners", value: banners.length },
          { label: "Active",        value: banners.filter((b) => b.status === "Active").length },
          { label: "Inactive",      value: banners.filter((b) => b.status === "Inactive").length },
        ].map((s) => (
          <div key={s.label} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
            <p className="text-3xl font-extrabold text-gray-900">{s.value}</p>
            <p className="text-sm text-gray-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Banner Preview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {banners.map((banner) => (
          <div key={banner.id} className={`relative rounded-2xl overflow-hidden shadow-sm border border-white/20 ${banner.status === "Inactive" ? "opacity-50" : ""}`}>
            <div className={`bg-gradient-to-r ${banner.bg} p-6 text-white`}>
              <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded-full uppercase tracking-wide">
                {banner.position}
              </span>
              <h3 className="mt-3 text-lg font-extrabold leading-tight">{banner.title}</h3>
              <p className="mt-1 text-sm text-white/80">{banner.subtitle}</p>
              <div className="flex items-center gap-1 mt-3 text-xs text-white/70">
                <LinkIcon size={12} />
                <span>{banner.link}</span>
              </div>
            </div>
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-100">
              <span className={`flex items-center gap-1.5 text-xs font-bold ${
                banner.status === "Active" ? "text-green-600" : "text-gray-400"
              }`}>
                <span className={`w-2 h-2 rounded-full ${banner.status === "Active" ? "bg-green-500" : "bg-gray-300"}`} />
                {banner.status}
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => toggleStatus(banner.id)}
                  className="p-1.5 hover:bg-gray-100 rounded-lg transition"
                  title="Toggle status"
                >
                  {banner.status === "Active"
                    ? <ToggleRight size={20} className="text-orange-500" />
                    : <ToggleLeft size={20} className="text-gray-400" />
                  }
                </button>
                <button onClick={() => handleOpen(banner)} className="p-1.5 hover:bg-orange-50 rounded-lg text-orange-500 transition">
                  <Edit2 size={16} />
                </button>
                <button onClick={() => handleDelete(banner.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400 transition">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <h2 className="text-xl font-extrabold text-gray-900">{editingId ? "Edit Banner" : "New Banner"}</h2>
              <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Title</label>
                <input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Banner headline..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Subtitle</label>
                <input
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="Supporting text..."
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Link URL</label>
                <input
                  value={formData.link}
                  onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                  placeholder="/menu"
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Position</label>
                  <select
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 transition"
                  >
                    {POSITIONS.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 transition"
                  >
                    <option>Active</option>
                    <option>Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Background</label>
                <div className="flex gap-2 flex-wrap">
                  {BG_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, bg: opt.value })}
                      className={`w-8 h-8 rounded-full bg-gradient-to-r ${opt.value} border-2 transition ${
                        formData.bg === opt.value ? "border-gray-900 scale-110" : "border-transparent"
                      }`}
                      title={opt.label}
                    />
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className={`rounded-xl overflow-hidden bg-gradient-to-r ${formData.bg} p-4 text-white`}>
                <p className="font-extrabold text-sm">{formData.title || "Banner Preview"}</p>
                <p className="text-xs text-white/80 mt-0.5">{formData.subtitle || "Subtitle goes here"}</p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold shadow-lg shadow-orange-200 transition active:scale-95">
                  {editingId ? "Save Changes" : "Add Banner"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Banners;
